import React, { useCallback, useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";
import Modal from "../Modal";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fetchCompanies, type Company } from "../../services/adminApi";
import {
  type ConsignmentRequester,
  deleteConsignmentRequester,
  listConsignmentRequesters,
  upsertConsignmentRequester,
} from "../../services/consignmentApi";

/**
 * Consignment Requesters tab (super-admin, companies.manage). Owns the
 * per-(customer, workcenter) `requester_id` mapping the weekly
 * consignment report emits as the `requester` field. Backed by
 * `mttr.consignment_requester` (migration 090). Falls back to the
 * customer's `consignment_requester_default` (set in Companies) when
 * no row exists for the (customer, workcenter) pair.
 */
export const RequestersTab: React.FC = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState<ConsignmentRequester[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filterCustomer, setFilterCustomer] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ConsignmentRequester | "new" | null>(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    setError(null);
    try {
      const customer = filterCustomer === "" ? undefined : Number(filterCustomer);
      const r = await listConsignmentRequesters(token, customer);
      setRows(r.requesters);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
    }
  }, [token, filterCustomer]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!token) return;
    fetchCompanies(token)
      .then((cs) => setCompanies(cs.filter((c) => c.company_type === "customer")))
      .catch(() => setCompanies([]));
  }, [token]);

  const onDelete = async (r: ConsignmentRequester): Promise<void> => {
    if (!token) return;
    if (!confirm(
      `Remove requester ${r.requester_id} for ${r.customer_name ?? r.customer_company_id} / ${r.workcenter_code ?? r.workcenter_id}?`,
    )) return;
    try {
      await deleteConsignmentRequester(token, r.customer_company_id, r.workcenter_id);
      await refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-semibold text-secondary-800">
            Consignment Requesters
          </h2>
          <p className="text-xs text-secondary-500 mt-0.5">
            Per-(customer, workcenter) requester id emitted on the weekly
            consignment report. Falls back to the customer's
            <code className="mx-1">consignment_requester_default</code>
            (Companies tab) when no row exists for the pair.
          </p>
        </div>
        <Button size="sm" onClick={() => setEditing("new")}>
          + Add requester
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <label className="text-xs text-secondary-600">
          Filter by customer:
        </label>
        <select
          className="px-2 py-1 border border-secondary-300 rounded text-sm bg-white"
          value={filterCustomer}
          onChange={(e) =>
            setFilterCustomer(e.target.value ? Number(e.target.value) : "")
          }
        >
          <option value="">All customers</option>
          {companies.map((c) => (
            <option key={c.company_id} value={c.company_id}>
              {c.company_name}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {!rows ? (
        <LoadingSpinner label="loading requesters" />
      ) : (
        <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Workcenter</th>
                <th className="text-left px-3 py-2">Requester id</th>
                <th className="text-left px-3 py-2">Note</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.customer_company_id}:${r.workcenter_id}`}
                  className="border-t border-secondary-100"
                >
                  <td className="px-3 py-2 font-medium">
                    {r.customer_name ?? `#${r.customer_company_id}`}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-secondary-700">
                    {r.workcenter_code ?? `#${r.workcenter_id}`}
                    {r.workcenter_name && (
                      <span className="text-secondary-500 ml-2 font-sans not-italic">
                        {r.workcenter_name}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono">{r.requester_id}</td>
                  <td className="px-3 py-2 text-xs text-secondary-600">
                    {r.note ?? ""}
                  </td>
                  <td className="px-3 py-2 text-xs text-secondary-500">
                    {r.set_by} · {r.set_utc?.slice(0, 19).replace("T", " ") ?? ""}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button
                      type="button"
                      className="text-xs text-primary-600 hover:underline"
                      onClick={() => setEditing(r)}
                    >
                      edit
                    </button>
                    <button
                      type="button"
                      className="text-xs text-error-600 hover:underline"
                      onClick={() => void onDelete(r)}
                    >
                      remove
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-secondary-500 text-sm">
                    No (customer, workcenter) requesters configured.
                    {filterCustomer === "" && companies.length > 0 && (
                      <> Click <strong>+ Add requester</strong> to start.</>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <RequesterEditModal
          token={token ?? ""}
          existing={editing === "new" ? null : editing}
          customers={companies}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
};

// ---------- Edit modal ----------

const inputCls = "w-full border border-secondary-300 rounded px-2 py-1.5 text-sm";

const RequesterEditModal: React.FC<{
  token: string;
  existing: ConsignmentRequester | null;
  customers: Company[];
  onClose: () => void;
  onDone: () => void;
}> = ({ token, existing, customers, onClose, onDone }) => {
  const editing = existing !== null;

  const [customerId, setCustomerId] = useState<number | "">(
    existing?.customer_company_id ?? "",
  );
  const [workcenterId, setWorkcenterId] = useState<string>(
    existing ? String(existing.workcenter_id) : "",
  );
  const [requesterId, setRequesterId] = useState<string>(
    existing?.requester_id ?? "",
  );
  const [note, setNote] = useState<string>(existing?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setErr(null);
    if (customerId === "") return setErr("Customer is required.");
    const wid = Number(workcenterId);
    if (!Number.isFinite(wid) || wid <= 0) {
      return setErr("Workcenter id must be a positive integer (md_workcenter.id).");
    }
    if (!requesterId.trim()) return setErr("Requester id is required.");
    setBusy(true);
    try {
      await upsertConsignmentRequester(token, {
        customer_company_id: Number(customerId),
        workcenter_id: wid,
        requester_id: requesterId.trim(),
        note: note.trim() || undefined,
      });
      onDone();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? "Edit requester" : "Add requester"}
      widthClass="max-w-lg"
    >
      <div className="space-y-3">
        {err && (
          <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{err}</div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
              Customer
            </span>
            <select
              className={inputCls + " bg-white"}
              value={customerId}
              disabled={editing}
              onChange={(e) =>
                setCustomerId(e.target.value ? Number(e.target.value) : "")
              }
            >
              <option value="">Select…</option>
              {customers.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {c.company_name}
                </option>
              ))}
            </select>
            {editing && (
              <span className="text-[11px] text-secondary-500">
                customer + workcenter form the primary key; create a new
                row instead of changing them
              </span>
            )}
          </label>

          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
              Workcenter id (md_workcenter.id)
            </span>
            <input
              className={inputCls + " font-mono"}
              type="number"
              min={1}
              value={workcenterId}
              disabled={editing}
              onChange={(e) => setWorkcenterId(e.target.value)}
              placeholder="e.g. 707"
            />
          </label>
        </div>

        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
            Requester id (downstream WMS user)
          </span>
          <input
            className={inputCls + " font-mono"}
            value={requesterId}
            maxLength={64}
            onChange={(e) => setRequesterId(e.target.value)}
            placeholder="e.g. 100209405"
          />
        </label>

        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
            Note (optional)
          </span>
          <input
            className={inputCls}
            value={note}
            maxLength={255}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. RIG 00B06 ND - McKenzie · contact Joe"
          />
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RequestersTab;
